import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import PushToggle from "../../components/PushToggle";

export default function Profil() {
  const { user, refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "gewerbe" ? "gewerbe" : "profil";
  const [tab, setTab] = useState(initialTab);
  const [form, setForm] = useState({ name: user.name, telefon: user.telefon || "", anlagenadresse: user.anlagenadresse || "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await api.put("/me/profil", form);
      await refresh();
      setMsg("Gespeichert");
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="profil-page">
      <div>
        <h1 className="text-3xl font-heading font-bold text-falke-blue">Profil & Einstellungen</h1>
      </div>

      <div className="inline-flex bg-white rounded-xl p-1 border border-falke-blue/10 shadow-sm">
        <button onClick={() => setTab("profil")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "profil" ? "bg-falke-orange text-white" : "text-falke-blue"}`} data-testid="tab-profil">Stammdaten</button>
        {user.tippgeber_modus === "privat" && (
          <button onClick={() => setTab("gewerbe")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "gewerbe" ? "bg-falke-orange text-white" : "text-falke-blue"}`} data-testid="tab-gewerbe">Gewerblicher Tippgeber</button>
        )}
        {user.tippgeber_modus !== "privat" && (
          <span className="px-4 py-2 text-sm text-falke-blue/60">Tippgeber-Status: <strong>{user.tippgeber_modus}</strong></span>
        )}
      </div>

      {tab === "profil" && (
        <div className="falke-card p-6 max-w-2xl space-y-4" data-testid="profil-form">
          <div>
            <label className="block text-sm font-medium text-falke-blue mb-1.5">Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-falke-blue/15" data-testid="profil-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-falke-blue mb-1.5">Telefon</label>
            <input value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-falke-blue/15" data-testid="profil-tel" />
          </div>
          <div>
            <label className="block text-sm font-medium text-falke-blue mb-1.5">Anlagenadresse</label>
            <input value={form.anlagenadresse} onChange={(e) => setForm({ ...form, anlagenadresse: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-falke-blue/15" data-testid="profil-addr" />
          </div>
          <div className="text-sm text-falke-anthracite/60">E-Mail: {user.email} (nicht änderbar)</div>
          <div className="flex gap-3 items-center">
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="profil-save">{saving ? "Speichert…" : "Speichern"}</button>
            {msg && <span className="text-sm text-falke-blue">{msg}</span>}
          </div>
          <div className="mt-6 p-4 rounded-xl bg-falke-warmwhite border border-falke-blue/10">
            <div className="text-xs text-falke-blue/70 uppercase tracking-wider">Dein Empfehlungs-Link</div>
            <div className="text-falke-blue text-sm font-medium break-all">{window.location.origin}/r/{user.empfehlungs_link_slug}</div>
          </div>
        </div>
      )}

      {tab === "profil" && <PushToggle />}

      {tab === "gewerbe" && user.tippgeber_modus === "privat" && <GewerbeAntragForm />}
    </div>
  );
}

function GewerbeAntragForm() {
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    firmenname: "", firmenadresse: "", steuernummer: "", ust_id_nr: "",
    kleinunternehmer: false, iban: "", bic: "", kontoinhaber: "",
  });
  const [file, setFile] = useState(null);
  const [uploadMsg, setUploadMsg] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setMsg("");
    try {
      if (file) {
        setUploadMsg("Lade Gewerbeschein hoch…");
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/me/gewerbeschein-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setUploadMsg("✓ Gewerbeschein hochgeladen");
      }
      await api.post("/me/gewerbe-antrag", form);
      setMsg("Antrag eingereicht! Wir prüfen ihn schnellstmöglich.");
      await refresh();
    } catch (err) {
      setMsg(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="falke-card p-6 max-w-2xl space-y-4" data-testid="gewerbe-form">
      <div className="p-4 rounded-xl bg-falke-blue/[0.05] border border-falke-blue/10 text-sm text-falke-blue">
        Als gewerblicher Tippgeber erhältst du eine Geld-Vergütung pro abgeschlossener Empfehlung statt FalkeCoins. Die genaue Höhe wird dir nach Admin-Freigabe im Tippgeber-Cockpit angezeigt.
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {[
          ["firmenname", "Firmenname *", true],
          ["steuernummer", "Steuernummer *", true],
        ].map(([k, l, req]) => (
          <Field key={k} label={l}><input required={req} value={form[k]} onChange={upd(k)} className="reg-input" data-testid={`gewerbe-${k}`} /></Field>
        ))}
      </div>
      <Field label="Firmenadresse *"><input required value={form.firmenadresse} onChange={upd("firmenadresse")} className="reg-input" data-testid="gewerbe-firmenadresse" /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="USt-IdNr (optional)"><input value={form.ust_id_nr} onChange={upd("ust_id_nr")} className="reg-input" data-testid="gewerbe-ustid" /></Field>
        <label className="flex items-center gap-2 self-end pb-2">
          <input type="checkbox" checked={form.kleinunternehmer} onChange={upd("kleinunternehmer")} className="w-4 h-4 accent-falke-orange" data-testid="gewerbe-klein" />
          <span className="text-sm">Kleinunternehmer (§19 UStG)</span>
        </label>
        <Field label="IBAN *"><input required value={form.iban} onChange={upd("iban")} className="reg-input" data-testid="gewerbe-iban" /></Field>
        <Field label="BIC *"><input required value={form.bic} onChange={upd("bic")} className="reg-input" data-testid="gewerbe-bic" /></Field>
      </div>
      <Field label="Kontoinhaber *"><input required value={form.kontoinhaber} onChange={upd("kontoinhaber")} className="reg-input" data-testid="gewerbe-inhaber" /></Field>
      <div>
        <label className="block text-sm font-medium text-falke-blue mb-1.5">Gewerbeschein (PDF, JPG oder PNG, max 10 MB) *</label>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm" data-testid="gewerbe-file" />
        {file && <div className="text-xs text-falke-blue mt-1">{file.name} ({(file.size / 1024).toFixed(0)} KB)</div>}
        {uploadMsg && <div className="text-xs text-falke-blue mt-1">{uploadMsg}</div>}
      </div>
      <button type="submit" disabled={loading} className="btn-primary" data-testid="gewerbe-submit">{loading ? "Sende…" : "Antrag einreichen"}</button>
      {msg && <div className="text-sm text-falke-success">{msg}</div>}
      <style>{`.reg-input { width: 100%; padding: 10px 14px; border-radius: 10px; border: 1px solid rgba(45,92,126,0.15); }`}</style>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-falke-blue mb-1.5">{label}</label>
      {children}
    </div>
  );
}
